import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  FileDown, DollarSign, FileText, Users, Layers, ShieldCheck,
  TrendingUp, CircleDollarSign, ChevronDown, Sparkles, Lightbulb,
  AlertTriangle, CheckCircle2, BookOpen, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#0f172a', '#2563eb', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777', '#475569', '#84cc16', '#f59e0b', '#6366f1', '#14b8a6', '#f43f5e', '#a855f7'];
const PIE_COLORS = ['#dc2626', '#d97706', '#2563eb', '#059669', '#0f172a'];

const fmt = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtK = (v) => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v.toFixed(0)}`;

const SummaryCard = ({ icon: Icon, label, value, sub, color = '#0f172a' }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5 flex items-start gap-4" data-testid={`report-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}12` }}>
      <Icon size={20} style={{ color }} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-xl font-bold text-slate-900 mt-0.5 truncate">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-6">
    <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-5">{title}</h3>
    {children}
  </div>
);

const CustomTooltip = ({ active, payload, label, prefix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white text-xs rounded-md px-3 py-2 shadow-lg">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-slate-300">
          {p.name}: {prefix}{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

const REC_SECTIONS = [
  { key: 'data_collection_improvements', label: 'Data Collection Improvements', icon: Lightbulb, color: '#2563eb', bg: 'bg-blue-50', text: 'text-blue-700' },
  { key: 'standardization_suggestions', label: 'Standardization Suggestions', icon: BookOpen, color: '#059669', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  { key: 'preliminary_classification_tips', label: 'Classification Tips', icon: Layers, color: '#7c3aed', bg: 'bg-violet-50', text: 'text-violet-700' },
  { key: 'data_quality_issues', label: 'Data Quality Issues', icon: AlertTriangle, color: '#d97706', bg: 'bg-amber-50', text: 'text-amber-700' },
  { key: 'best_practices', label: 'Best Practices', icon: CheckCircle2, color: '#0891b2', bg: 'bg-cyan-50', text: 'text-cyan-700' },
];

const RecommendationsDisplay = ({ recs }) => {
  if (!recs) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="recommendations-grid">
      {REC_SECTIONS.map(({ key, label, icon: Icon, color, bg, text }) => {
        const items = recs[key];
        if (!items || !items.length) return null;
        return (
          <div key={key} className={`${bg} rounded-lg p-5 border border-opacity-20`} style={{ borderColor: color }} data-testid={`rec-${key}`}>
            <div className="flex items-center gap-2 mb-3">
              <Icon size={16} className={text} />
              <h4 className={`text-xs font-semibold uppercase tracking-wide ${text}`}>{label}</h4>
            </div>
            <ul className="space-y-2">
              {items.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className={`${text} mt-1 shrink-0 text-xs font-bold`}>{i + 1}.</span>
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

export const Reports = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [recsLoading, setRecsLoading] = useState(false);
  const [recsGenerating, setRecsGenerating] = useState(false);
  const printRef = useRef();

  const fetchData = useCallback(async (uploadId) => {
    setLoading(true);
    setRecommendations(null);
    try {
      const params = uploadId ? { upload_id: uploadId } : {};
      const res = await axios.get(`${API}/reports/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(res.data);

      // Fetch cached recommendations if session selected
      if (uploadId) {
        setRecsLoading(true);
        try {
          const recsRes = await axios.get(`${API}/spend/recommendations/${uploadId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (recsRes.data.generated) {
            setRecommendations(recsRes.data);
          }
        } catch { /* no cached recs */ }
        finally { setRecsLoading(false); }
      }
    } catch (err) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(selectedSession || undefined); }, [fetchData, selectedSession]);

  const handleDownloadPDF = async () => {
    if (!selectedSession) {
      toast.error('Select a specific session to download its PDF report');
      return;
    }
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/spend/report/${selectedSession}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `DataRefinery_Report_${selectedSession.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('PDF generation failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleGenerateRecs = async () => {
    if (!selectedSession) return;
    setRecsGenerating(true);
    try {
      const res = await axios.post(`${API}/spend/recommendations/${selectedSession}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecommendations(res.data);
      toast.success('AI recommendations generated');
    } catch {
      toast.error('Failed to generate recommendations');
    } finally {
      setRecsGenerating(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reports-loading">
        <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" />
      </div>
    );
  }

  const { summary, spend_by_category, spend_by_supplier, monthly_trend, confidence_distribution, sessions } = data;
  const cashData = [
    { name: 'Cash', value: summary.cash_count },
    { name: 'Non-Cash', value: summary.non_cash_count },
  ].filter(d => d.value > 0);
  const coverageData = [
    { level: 'L2', pct: summary.l2_coverage },
    { level: 'L3', pct: summary.l3_coverage },
    { level: 'L4', pct: summary.l4_coverage },
  ];

  return (
    <div ref={printRef} data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Comprehensive spend intelligence &amp; analytics</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <select
              data-testid="report-session-selector"
              value={selectedSession}
              onChange={(e) => setSelectedSession(e.target.value)}
              className="appearance-none bg-white border border-slate-200 rounded-md pl-3 pr-8 py-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400 cursor-pointer"
            >
              <option value="">All Sessions (Cumulative)</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.filename || s.id.slice(0, 8)} ({s.total_records} records)
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          <Button
            data-testid="download-pdf-button"
            onClick={handleDownloadPDF}
            disabled={downloading || !selectedSession}
            className="bg-[#0f172a] hover:bg-[#1e293b] text-white text-sm"
          >
            <FileDown size={16} className="mr-1.5" />
            {downloading ? 'Generating...' : 'Download PDF'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="report-summary-cards">
        <SummaryCard icon={DollarSign} label="Total Spend" value={fmt(summary.total_spend)} sub={`${summary.total_records} records`} color="#059669" />
        <SummaryCard icon={Users} label="Suppliers" value={summary.unique_suppliers} sub={`${summary.unique_categories} categories`} color="#2563eb" />
        <SummaryCard icon={ShieldCheck} label="Avg Confidence" value={`${summary.avg_confidence}%`} sub={`${summary.classified_count} classified`} color="#0891b2" />
        <SummaryCard icon={Layers} label="Cleansing Quality" value={`${summary.avg_cleansing_quality}%`} sub={`${summary.cleansed_count} cleansed`} color="#d97706" />
      </div>

      {/* Row 1: Spend by Category + Monthly Trend */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <Section title="Spend by Category (L1)">
          {spend_by_category.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={spend_by_category} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" tickFormatter={fmtK} tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis type="category" dataKey="category" width={120} tick={{ fontSize: 11, fill: '#334155' }} />
                <Tooltip content={<CustomTooltip prefix="$" />} />
                <Bar dataKey="spend" name="Spend" radius={[0, 4, 4, 0]}>
                  {spend_by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 py-8 text-center">No classified data</p>}
        </Section>

        <Section title="Monthly Spend Trend">
          {monthly_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={monthly_trend} margin={{ left: 10, right: 20, top: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip content={<CustomTooltip prefix="$" />} />
                <Line type="monotone" dataKey="spend" name="Spend" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 4, fill: '#2563eb' }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 py-8 text-center">No date data available</p>}
        </Section>
      </div>

      {/* Row 2: Top Suppliers + Confidence + Cash */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
        <div className="xl:col-span-1">
          <Section title="Top Suppliers by Spend">
            {spend_by_supplier.length > 0 ? (
              <div className="space-y-2.5 max-h-[340px] overflow-y-auto pr-1">
                {spend_by_supplier.map((s, i) => {
                  const pct = summary.total_spend > 0 ? (s.spend / summary.total_spend) * 100 : 0;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 w-5 text-right shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline mb-1">
                          <span className="text-xs font-medium text-slate-700 truncate mr-2">{s.supplier}</span>
                          <span className="text-xs text-slate-500 shrink-0">{fmt(s.spend)}</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5">
                          <div className="h-1.5 rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-sm text-slate-400 py-8 text-center">No supplier data</p>}
          </Section>
        </div>

        <div className="xl:col-span-1">
          <Section title="Classification Confidence">
            {confidence_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={confidence_distribution} dataKey="count" nameKey="range" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} label={({ range, count }) => `${range}: ${count}`}>
                    {confidence_distribution.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-slate-400 py-8 text-center">No confidence data</p>}
            <div className="mt-3 space-y-1.5">
              {coverageData.map(c => (
                <div key={c.level} className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500 w-6">{c.level}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2">
                    <div className="h-2 rounded-full bg-[#2563eb]" style={{ width: `${Math.min(c.pct, 100)}%` }} />
                  </div>
                  <span className="text-slate-600 font-medium w-10 text-right">{c.pct}%</span>
                </div>
              ))}
            </div>
          </Section>
        </div>

        <div className="xl:col-span-1">
          <Section title="Payment Method Split">
            {cashData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={cashData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={4}>
                      <Cell fill="#d97706" />
                      <Cell fill="#0f172a" />
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="bg-amber-50 rounded-md p-3 text-center">
                    <p className="text-lg font-bold text-amber-700">{summary.cash_count}</p>
                    <p className="text-xs text-amber-600">Cash Payments</p>
                  </div>
                  <div className="bg-slate-50 rounded-md p-3 text-center">
                    <p className="text-lg font-bold text-slate-700">{summary.non_cash_count}</p>
                    <p className="text-xs text-slate-500">Non-Cash</p>
                  </div>
                </div>
              </>
            ) : <p className="text-sm text-slate-400 py-8 text-center">No payment data</p>}
          </Section>
        </div>
      </div>

      {/* Row 3: Category Table + Sessions */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <Section title="Category Breakdown">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="category-breakdown-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Category</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Spend</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Records</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Confidence</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">% Total</th>
                </tr>
              </thead>
              <tbody>
                {spend_by_category.map((c, i) => {
                  const pct = summary.total_spend > 0 ? (c.spend / summary.total_spend * 100) : 0;
                  return (
                    <tr key={c.category || `cat-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2.5 px-3 font-medium text-slate-700">{c.category}</td>
                      <td className="py-2.5 px-3 text-right text-slate-600">{fmt(c.spend)}</td>
                      <td className="py-2.5 px-3 text-right text-slate-500">{c.count}</td>
                      <td className="py-2.5 px-3 text-right">
                        <span className={`text-xs font-medium ${c.avg_confidence >= 80 ? 'text-emerald-600' : c.avg_confidence >= 60 ? 'text-amber-600' : 'text-red-500'}`}>
                          {c.avg_confidence}%
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right text-slate-500">{pct.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="Processing Sessions">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sessions-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">File</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Records</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s, i) => (
                  <tr key={s.id || `session-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2.5 px-3 font-medium text-slate-700 truncate max-w-[200px]">{s.filename || s.id.slice(0, 8)}</td>
                    <td className="py-2.5 px-3 text-right text-slate-600">{s.total_records}</td>
                    <td className="py-2.5 px-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        s.status === 'classified' ? 'bg-emerald-50 text-emerald-700' :
                        s.status === 'cleansed' ? 'bg-blue-50 text-blue-700' :
                        s.status === 'uploaded' ? 'bg-slate-100 text-slate-600' :
                        'bg-amber-50 text-amber-700'
                      }`}>{s.status}</span>
                    </td>
                    <td className="py-2.5 px-3 text-right text-slate-500 text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      {/* AI Recommendations */}
      <div className="mb-6" data-testid="ai-recommendations-section">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
                <Sparkles size={18} className="text-violet-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">AI Data Improvement Recommendations</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {recommendations?.created_at ? `Generated ${new Date(recommendations.created_at).toLocaleString()}` : 'Powered by GPT-5.2 analysis'}
                </p>
              </div>
            </div>
            {selectedSession && (
              <Button
                data-testid="generate-recommendations-btn"
                onClick={handleGenerateRecs}
                disabled={recsGenerating || recsLoading}
                variant={recommendations ? 'outline' : 'default'}
                className={recommendations ? 'text-sm' : 'bg-[#0f172a] hover:bg-[#1e293b] text-white text-sm'}
                size="sm"
              >
                {recsGenerating ? (
                  <><RefreshCw size={14} className="mr-1.5 animate-spin" /> Analyzing...</>
                ) : recommendations ? (
                  <><RefreshCw size={14} className="mr-1.5" /> Regenerate</>
                ) : (
                  <><Sparkles size={14} className="mr-1.5" /> Generate AI Insights</>
                )}
              </Button>
            )}
          </div>

          {!selectedSession ? (
            <div className="bg-slate-50 rounded-lg p-8 text-center">
              <Sparkles size={28} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Select a specific session above to generate AI-powered data improvement recommendations</p>
            </div>
          ) : recsLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin w-6 h-6 border-2 border-slate-300 border-t-slate-900 rounded-full" />
            </div>
          ) : !recommendations ? (
            <div className="bg-gradient-to-br from-violet-50 to-slate-50 rounded-lg p-8 text-center">
              <Sparkles size={28} className="mx-auto text-violet-400 mb-3" />
              <p className="text-sm text-slate-600 mb-1">No recommendations generated yet for this session</p>
              <p className="text-xs text-slate-400">Click "Generate AI Insights" to analyze your data and get improvement suggestions</p>
            </div>
          ) : (
            <RecommendationsDisplay recs={recommendations.recommendations} />
          )}
        </div>
      </div>
    </div>
  );
};
